import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  MateriaCreateInput,
  type MateriaCreateInputType,
  type MateriaOutputType,
} from '@shared/dto';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { useApiMutation, useApiQuery } from '../../../hooks/use-api';
import { materiasApi } from '../../../api/materias.api';
import { usePainelSnapshot } from '../../../hooks/use-painel-snapshot';
import { ConteudosDaMateria } from './conteudos-da-materia';

export function MateriasTab() {
  const { data: materias, loading, refetch } = useApiQuery(materiasApi.listar, undefined);
  const { refetch: refetchPainel } = usePainelSnapshot();

  function aoAtualizar() {
    refetch();
    refetchPainel();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        {materias ? (
          <p className="text-sm text-muted-foreground">{materias.length} matéria(s)</p>
        ) : (
          <Skeleton className="h-4 w-24" />
        )}
        <NovaMateriaDialog onCriada={aoAtualizar} />
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : null}

      {!loading && materias?.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma matéria cadastrada ainda.</p>
      ) : null}

      {materias && materias.length > 0 ? (
        <Accordion type="multiple" className="rounded-lg border">
          {materias.map((materia) => (
            <MateriaAccordionItem key={materia.id} materia={materia} onAtualizada={aoAtualizar} />
          ))}
        </Accordion>
      ) : null}
    </div>
  );
}

function NovaMateriaDialog({ onCriada }: { onCriada: () => void }) {
  const [open, setOpen] = useState(false);
  const { mutate, loading } = useApiMutation(materiasApi.criar);
  const form = useForm<MateriaCreateInputType>({
    resolver: zodResolver(MateriaCreateInput),
    defaultValues: { nome: '' },
  });

  async function onSubmit(dados: MateriaCreateInputType) {
    await mutate(dados);
    form.reset({ nome: '' });
    setOpen(false);
    onCriada();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="size-4" />
          Nova matéria
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova matéria</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={loading}>
                {loading ? 'Salvando...' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function MateriaAccordionItem({
  materia,
  onAtualizada,
}: {
  materia: MateriaOutputType;
  onAtualizada: () => void;
}) {
  const { mutate: remover, loading: removendo } = useApiMutation(materiasApi.remover);

  async function aoConfirmarRemocao() {
    await remover(materia.id);
    onAtualizada();
  }

  return (
    <AccordionItem value={materia.id}>
      <div className="flex items-center gap-3 px-4">
        <AccordionTrigger className="flex-1">{materia.nome}</AccordionTrigger>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remover matéria"
              disabled={removendo}
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remover &quot;{materia.nome}&quot;?</AlertDialogTitle>
              <AlertDialogDescription>
                A matéria deixa de aparecer em qualquer lista. Essa ação não pode ser desfeita por
                aqui.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={() => void aoConfirmarRemocao()}>
                Remover
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <AccordionContent className="px-4">
        <ConteudosDaMateria materiaId={materia.id} />
      </AccordionContent>
    </AccordionItem>
  );
}
